import React, { useMemo, useCallback } from 'react';
import cn from 'classnames';
import { isSameDay, set } from 'date-fns';

import { useScheduleDemoContext } from '@framework/ui/Organisms/ScheduleDemo/ScheduleDemoContext';
import { useTimeZoneContext } from '@framework/timezone/TimeZoneContext';
import FinalForm, { Field, type TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm';
import ReCaptcha from '@framework/ui/Molecules/Form/ReCaptcha';
import TimeZoneSelect from '@framework/ui/Organisms/FinalForm/Fields/TimeZoneSelect';
import DatePicker from '@dealroadshow/uikit-picker/DatePicker';
import BaseDatePicker from '@dealroadshow/uikit-picker/BaseDatePicker';
import PhoneInput from '@dealroadshow/uikit-input/PhoneInput';
import Input from '@dealroadshow/uikit-input';
import IconClock from '@dealroadshow/uikit-icon/IconClock';
import IconEmail from '@dealroadshow/uikit-icon/IconEmail';
import Textarea from '@dealroadshow/uikit-textarea';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';

import {
  SCHEDULE_DEMO_FORM_NAME,
  TIME_INTERVAL,
} from './constants';
import {
  WEEKDAY_MONTH_DATE_FORMAT,
  TIME_FORMAT_12,
} from '@dealroadshow/uikit-date-time/dateFormats';
import { guessTimeZone, getTimeZoneDisplayNameDate } from '@framework/timezone/helpers';
import { IScheduleDemoFormValues } from '@framework/ui/Organisms/ScheduleDemo/ScheduleDemoForm/interfaces';
import {
  getNowDateRounded,
  getScheduleDemoFormTimeValues,
} from './helpers';
import utcToZonedDate from '@framework/date-helpers/utcToZonedDate';
import getUnixTimestamp from '@framework/date-helpers/getUnixTimestamp';
import getDateFromUnixTime from '@framework/date-helpers/getDateFromUnixTime';
import validator from './validator';

import styles from './scheduleDemoForm.module.scss';
import spacesStyles from '@dealroadshow/uikit-styles/helpers/spaces.module.scss';

interface IProps {
  tenant?: string,
  withLabels?: boolean,
  submitButtonClassName?: string,
  buttonTitle?: string | React.ReactNode,
  isSubmitting?: boolean,
  getInitialFormValues?: () => IScheduleDemoFormValues | null,
  submitButtonContainerClassName?: string,
  notesPlaceholder?: string,
  supportRecaptcha?: boolean,
  isPublicSubmit?: boolean,
  defaultPhoneCountry?: string,
}

const ScheduleDemoForm = (
  {
    tenant,
    getInitialFormValues,
    isPublicSubmit = true,
    supportRecaptcha = true,
    notesPlaceholder = 'Please provide additional details if applicable...',
    submitButtonClassName,
    submitButtonContainerClassName,
    withLabels,
    buttonTitle,
    isSubmitting,
    defaultPhoneCountry,
  }: IProps,
) => {
  const initialValues = useMemo(() => getInitialFormValues?.() ?? null, []);
  const {
    submitScheduleDemoForm,
    isFetching,
  } = useScheduleDemoContext();
  const {
    timeZoneList,
    timeZoneOptions,
  } = useTimeZoneContext();
  const onSubmit = useCallback(
    (formData: any) => submitScheduleDemoForm({
      ...formData,
      tenant,
    }, isPublicSubmit),
    [submitScheduleDemoForm, isPublicSubmit, tenant],
  );
  const validate = useCallback(
    (values: any) => validator(values, supportRecaptcha),
    [supportRecaptcha],
  );

  const formInitialValues = useMemo(
    () => {
      const guessedTimeZone = guessTimeZone(timeZoneList);
      const initialTimeZone = initialValues?.timeZone || (
        timeZoneList.length
          ? {
            // @ts-ignore
            timeZone: guessedTimeZone.timeZone,
            // @ts-ignore
            value: guessedTimeZone.id,
            // @ts-ignore
            label: getTimeZoneDisplayNameDate(guessedTimeZone),
          }
          : null
      );

      const now = getUnixTimestamp(getNowDateRounded(initialTimeZone?.timeZone));

      return {
        dateAt: now,
        timeAt: now,
        timeZone: initialTimeZone,
        ...initialValues,
      };
    },
    [timeZoneList.length, initialValues],
  );

  const renderFields = useCallback<TFinalFormRenderProp>(
    (
      {
        invalid,
        values: {
          dateAt: dateAtFormValue,
          timeZone: timeZoneFormValue,
        },
      },
    ) => {
      const {
        now,
        timeAtFieldMinTimeValue,
      } = getScheduleDemoFormTimeValues({
        dateAtFormValue,
        timeZoneFormValue,
      });

      const filterPassedTime = (time: number | string | Date) => {
        const currentDate = utcToZonedDate(timeZoneFormValue?.timeZone);
        const selectedDate = new Date(time);
        if (isSameDay(selectedDate, getDateFromUnixTime(dateAtFormValue))) {
          return currentDate.getTime() <= selectedDate.getTime();
        }

        return dateAtFormValue < selectedDate.getTime();
      };

      return (
        <div className={ styles.scheduleDemoForm }>
          <div className={ styles.halfWidthDesktop }>
            <Field
              name="firstName"
              label={ withLabels ? 'First Name' : undefined }
              placeholder="First Name"
              component={ Input }
              dataTest="scheduleDemoFormFirstNameInput"
            />
          </div>
          <div className={ styles.halfWidthDesktop }>
            <Field
              name="lastName"
              label={ withLabels ? 'Last Name' : undefined }
              placeholder="Last Name"
              component={ Input }
              dataTest="scheduleDemoFormLastNameInput"
            />
          </div>
          <div className={ styles.fullWidth }>
            <Field
              name="companyName"
              label={ withLabels ? 'Company Name' : undefined }
              placeholder="Company Name"
              component={ Input }
              dataTest="scheduleDemoFormCompanyNameInput"
            />
          </div>
          <div className={ styles.halfWidthDesktop }>
            <Field
              name="corporateEmail"
              label={ withLabels ? 'Corporate Email' : undefined }
              placeholder="Corporate Email"
              icon={ IconEmail }
              component={ Input }
              dataTest="scheduleDemoFormCorporateEmailInput"
            />
          </div>
          <div className={ styles.halfWidthDesktop }>
            <Field
              isFinalForm
              label={ withLabels ? 'Phone' : undefined }
              name="phone"
              placeholder="Phone"
              component={ PhoneInput }
              defaultCountry={ defaultPhoneCountry }
              dataTest="scheduleDemoFormPhoneInput"
            />
          </div>
          <div className={ cn(styles.halfWidthDesktop, spacesStyles.mbxl) }>
            <div className={ styles.halfWidth }>
              <div data-test="dateAt">
                <Field name="dateAt">
                  {
                    ({
                      meta,
                      input,
                    }) => (
                      <DatePicker
                        label={ withLabels ? 'Date' : undefined }
                        value={ getDateFromUnixTime(input.value) }
                        onChange={ (value) => value && input.onChange(getUnixTimestamp(value)) }
                        meta={ meta }
                        placeholder="Select Date"
                        dateFormat={ WEEKDAY_MONTH_DATE_FORMAT }
                        minDate={ now }
                        name={ input.name }
                        isReadOnly
                        isClearable={ false }
                        dataTest="scheduleDemoFormAtDatePicker"
                        inputDataTest="scheduleDemoFormDateAtInput"
                        isNarrow
                      />
                    )
                  }
                </Field>
              </div>
            </div>
            <div className={ styles.halfWidth }>
              <div data-test="timeAt">
                <Field name="timeAt">
                  {
                    ({
                      meta,
                      input,
                    }) => (
                      <BaseDatePicker
                        showTimeSelect
                        showTimeSelectOnly
                        className={ styles.timeAtSelect }
                        timeIntervals={ TIME_INTERVAL }
                        dateFormat={ TIME_FORMAT_12 }
                        selected={ getDateFromUnixTime(input.value) }
                        // @ts-ignore
                        onChange={ (value: Date) => input.onChange(getUnixTimestamp(value)) }
                        filterTime={ filterPassedTime }
                        minTime={ timeAtFieldMinTimeValue }
                        maxTime={ set(new Date(), {
                          hours: 23,
                          minutes: 46,
                        }) }
                        popperPlacement="bottom"
                        dataTest="scheduleDemoFormTimeAtDatePicker"
                        customInput={ (
                          <Input
                            // @ts-ignore
                            input={ { name: input.name } }
                            label={ withLabels ? 'Time' : undefined }
                            meta={ meta }
                            icon={ IconClock }
                            iconPosition="right"
                            isClearable={ false }
                            isReadOnly
                            isNarrow
                            dataTest="scheduleDemoFormTimeAtInput"
                          />
                        ) }
                      />
                    )
                  }
                </Field>
              </div>
            </div>
          </div>
          <div className={ cn(styles.halfWidthDesktop) }>
            <Field
              name="timeZone"
              label={ withLabels ? 'Timezone' : undefined }
              options={ timeZoneOptions }
              component={ TimeZoneSelect }
            />
          </div>
          <Field
            name="scheduledDemoType"
            type="hidden"
            component="input"
          />
          <div className={ cn(styles.fullWidth, styles.detailTextareaWrp) }>
            <Field
              name="notes"
              placeholder={ notesPlaceholder }
              rows={ 5 }
              component={ Textarea }
              dataTest="scheduleDemoFormNotesTextarea"
            />
          </div>
          { supportRecaptcha && (
            <div className={ cn(styles.fullWidth, styles.recaptchaWrp) }>
              <Field
                name="recaptchaToken"
                component={ ReCaptcha }
              />
            </div>
          ) }
          <div className={ cn(styles.submitBtnContainer, submitButtonContainerClassName) }>
            <Button
              type="submit"
              variant={ ButtonVariantType.action }
              className={ submitButtonClassName }
              disabled={ invalid }
              // @ts-ignore
              title={ buttonTitle || 'Submit Request' }
              dataTest="scheduleDemoSubmitButton"
            />
          </div>
        </div>
      );
    },
    [timeZoneOptions.length, isSubmitting],
  );

  if (isFetching) {
    return (
      <Spinner />
    );
  }

  return (
    <FinalForm
      dataTest={ SCHEDULE_DEMO_FORM_NAME }
      validate={ validate }
      onSubmit={ onSubmit }
      initialValues={ formInitialValues }
      render={ renderFields }
    />
  );
};

export default ScheduleDemoForm;
